import { useEffect } from "react";
import { ConnectionHandler } from "relay-runtime";
import { useDispatch, useSelector } from "react-redux";
import { uniqBy, isEqual, compact } from "lodash";

import {
  useResourcesListFilters,
  useResourcesListSorting
} from "../ResourcesList/resourcesList";
import { DEFAULT_SORT_ORDER_STATE } from "../../reducers/resourcesList/sortOrder";

import { storeContributionsConnection } from "../../reducers/contributionsConnections";

// This hook allows for tracking the current contributions connection id for the provided
// connectionType (contributions or markers) and connection (relay contributions
// connection).
//
// It ensures that when the calling component is mounted or its connection id is updated
// (for example when the contributions connection is refetched with new filters), the
// new connection id is stored in a global redux store's contributionsConnections key, to
// make it available to contribution validation mutations and their @prependEdge
// directives.
//
export const useStoreCurrentContributionsConnection = (
  contribuable,
  connectionType,
  connection,
  themeId = null
) => {
  const filters = useResourcesListFilters();
  const sort = useResourcesListSorting();

  const connectionId = connection?.__id;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!connectionId) return;

    dispatch(
      storeContributionsConnection(contribuable, connectionType, {
        connectionId,
        filters,
        sort,
        themeId
      })
    );
  }, [connectionType, connectionId, dispatch, contribuable, filters, sort, themeId]);
};

// This is the hook used to fetch back the currently active connection ids for both
// contributions and markers, which can then be passed down to the contribution validation
// mutation and ensure that any newly created contribution or marker edge after the
// mutation is added to the current active connections.
//
export const useCurrentContributionsConnections = contribuable => {
  const { id: contribuableId } = contribuable;

  const contributionsConnections = useSelector(
    ({ contributionsConnections }) => contributionsConnections[contribuableId]
  );

  if (!contributionsConnections) return {};

  // We deduplicate the connection ids as there may be multiple times the same connection
  // id, if multiple components use the same connection to fetch their contributions data.
  //
  const deduplicatedContributionsConnections = Object.keys(
    contributionsConnections
  ).reduce(
    (connections, connectionType) => ({
      ...connections,
      [connectionType]: uniqBy(
        contributionsConnections[connectionType],
        ({ connectionId }) => connectionId
      )
    }),
    {}
  );

  return deduplicatedContributionsConnections;
};

// This function allows prepending the contribution to the contributions connections that
// should display it, but are already cached by Relay. This allows displaying the
// contribution in the lists/graphs/maps without expiring the caches and without reloading
// the current contributions view.
//
export const addContributionEdgeToEligibleConnections = (
  store,
  serverEdge,
  connections,
  theme
) => {
  connections.forEach(connectionDetails => {
    const { connectionId, filters, sort, themeId: connectionThemeId } = connectionDetails;
    const filteredThemeIds = filters?.themeIds || [];

    const connectionThemeIds = compact(filteredThemeIds.concat([connectionThemeId]));
    const themeIds = compact([theme?.id, theme?.parent?.id, theme?.parent?.parent?.id]);

    // Don't reload the connection if it's bound to a theme id and the contribution has no
    // theme id or a different one, as the contribution shouldn't be part of the
    // connection.
    if (
      connectionThemeIds.length &&
      !connectionThemeIds.some(id => themeIds.includes(id))
    ) {
      return;
    }

    const connectionRecord = store.get(connectionId);

    const { bookmarks, search } = filters;
    if (
      bookmarks === true ||
      (search !== null && search !== "") ||
      !isEqual(sort, DEFAULT_SORT_ORDER_STATE)
    ) {
      return connectionRecord.invalidateRecord();
    }

    const newEdge = ConnectionHandler.buildConnectionEdge(
      store,
      connectionRecord,
      serverEdge
    );

    ConnectionHandler.insertEdgeBefore(connectionRecord, newEdge);
  });
};
